/**
 * Every PDF that went out, newest first (POS2-1137 CP1).
 *
 * The endpoint already excludes both things a customer must not see here: a test send, because a
 * test is not history, and a failed generation, because a report that was never made should not
 * appear as one that was. Neither is filtered again on this side — one rule, one place.
 *
 * **CP1 lists the sends; CP4 adds who each went to and the link to its file.** Per-send recipients
 * come from `report_deliveries` and not from the schedule's current list — the mock reuses the
 * live list for every past row, which would name people who were not on it at the time — so the
 * two columns arrive together with the endpoint that can answer them honestly.
 *
 * No open rate, no read rate, no delivery telemetry: not rendered here, and not asked for.
 */
import { Download } from "lucide-react";
import { toast } from "sonner";

import { useApiHeaders } from "@/hooks/use-api-headers";
import { useTranslation } from "@/i18n/use-translation";
import type { Locale } from "@/i18n/config";
import { cn } from "@/lib/utils";
import { REPORT_CATALOGUE } from "@/lib/report-catalogue";
import { runOutcome, sentAt, type ReportRun } from "@/hooks/use-reports";
import { avatarTint, initials } from "@/lib/report-recipients";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

/** POS2-1324 — a column headed "Sent" used to print `sent_at ?? generated_at ?? created_at`, so a
 * report still inside its collection window, and one whose send failed for good, both read as a
 * send at the time the PDF was built. The timestamp stays for the two non-sent states because it
 * is what an operator is looking for, but it is labelled as generation rather than as delivery.
 */
function SentCell({ run, locale, timezone }: { run: ReportRun; locale: Locale; timezone: string }) {
  const { t } = useTranslation();
  const outcome = runOutcome(run);

  if (outcome === "sent") return <>{sentAt(locale, run, timezone)}</>;

  return (
    <span className="flex flex-col gap-0.5">
      <span
        className={cn(
          "w-fit rounded px-1.5 py-0.5 text-xs",
          outcome === "failed" ? "bg-red-50 text-red-700" : "bg-gray-100 text-gray-600",
        )}
      >
        {outcome === "failed" ? t("reports.card.sendFailed") : t("reports.card.waitingToSend")}
      </span>
      <span className="text-xs text-gray-400">
        {t("reports.card.generatedAt", { time: sentAt(locale, run, timezone) })}
      </span>
    </span>
  );
}

export function RecentlySent({ runs, timezone }: { runs: ReportRun[]; timezone: string }) {
  const { locale, t } = useTranslation();
  const headers = useApiHeaders();

  async function open(run: ReportRun) {
    try {
      const res = await fetch(`${API_URL}/api/v1/reports/runs/${run.id}/pdf-url`, { headers });
      if (!res.ok) throw new Error("download failed");
      const json = await res.json();
      // Navigated to, never fetched (POS2-1256). The URL is R2's origin, and every request from
      // here carries `Authorization`, which makes it preflighted — and a preflighted cross-origin
      // request cannot follow a redirect to another origin, so the old fetch of a 302 route failed
      // before it got a response. The filename comes from the signed content-disposition, because
      // a `download` attribute is ignored on a cross-origin anchor; the browser saves rather than
      // navigating away, which is what the disposition is for.
      const anchor = document.createElement("a");
      anchor.href = json.data.url as string;
      anchor.click();
    } catch {
      toast.error(t("reports.history.downloadFailed"));
    }
  }

  return (
    <section className="rounded-lg border border-gray-200 bg-white">
      <div className="border-b border-gray-100 p-4">
        <h2 className="text-sm font-semibold text-gray-900">{t("reports.history.title")}</h2>
        <p className="mt-1 text-sm text-gray-500">{t("reports.history.subtitle")}</p>
      </div>

      {runs.length === 0 ? (
        <p className="p-4 text-sm text-gray-400">{t("reports.history.empty")}</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("reports.history.report")}</TableHead>
              <TableHead>{t("reports.history.period")}</TableHead>
              <TableHead>{t("reports.history.sent")}</TableHead>
              <TableHead>{t("reports.history.to")}</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {runs.map((run) => (
              <TableRow key={run.id}>
                <TableCell className="font-medium text-gray-900">
                  {reportName(t, run)}
                  {/* Without this a manual send and the scheduled one for the same period are two
                      identical rows, which reads as a duplicate-send bug rather than as the two
                      deliberate acts they are (POS2-1316). */}
                  {run.origin === "manual" && (
                    <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-xs font-normal text-gray-500">
                      {t("reports.history.sentManually")}
                    </span>
                  )}
                </TableCell>
                {/* `period_label` is English, written by the backend for CS and the history row.
                    Not translated: it names the window the PDF itself carries. */}
                <TableCell className="text-gray-500">{run.period_label}</TableCell>
                <TableCell className="text-gray-500">
                  <SentCell run={run} locale={locale} timezone={timezone} />
                </TableCell>
                <TableCell>
                  {run.recipients.length === 0 ? (
                    <span className="text-gray-400">—</span>
                  ) : (
                    <span className="flex -space-x-1.5">
                      {run.recipients.slice(0, 5).map((email) => (
                        <span
                          key={email}
                          className={`flex size-6 items-center justify-center rounded-full text-[10px] font-semibold ring-2 ring-white ${avatarTint(email)}`}
                          title={email}
                        >
                          {initials(email)}
                        </span>
                      ))}
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <button
                    type="button"
                    onClick={() => open(run)}
                    className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 hover:underline"
                  >
                    <Download className="size-3.5" aria-hidden="true" />
                    {t("reports.history.pdf")}
                  </button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </section>
  );
}

/** A run whose type is not in the catalogue is still a real send, so it prints its raw type rather
 * than vanishing from the customer's own history. */
function reportName(t: (key: string) => string, run: ReportRun): string {
  const entry = REPORT_CATALOGUE.find((e) => e.key === run.report_type);
  return entry ? t(entry.titleKey) : run.report_type;
}
